/**
 * FCL Export / Import report API helpers (Swagger-aligned).
 */

export const toApiDateTime = (dateStr, endOfDay = false) => {
    if (!dateStr) return undefined;
    const dateOnly = String(dateStr).split("T")[0];
    return endOfDay ? `${dateOnly}T23:59:59` : `${dateOnly}T00:00:00`;
};

/** Build query string for FCL report GET calls (skips empty values). */
export const buildReportQueryString = (params = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            search.set(key, String(value));
        }
    });
    return search.toString();
};

const tryParseJson = (value) => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return value;
    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
};

const REPORT_SKIP_ARRAY_KEYS = new Set([
    "containerTeusList",
    "ContainerTeusList",
    "salesPersonTeus",
    "SalesPersonTeus",
    "contactList",
    "ContactList",
    "carrierTeusList",
    "CarrierTeusList",
    "carrierList",
    "CarrierList",
    "bookingComplexitySummary_List",
    "BookingComplexitySummary_List",
    "bookingComplexitySummaryList",
    "BookingComplexitySummaryList",
]);

const REPORT_ROOT_META_KEYS = new Set([
    "statuscode",
    "statusCode",
    "StatusCode",
    "status",
    "Status",
    "message",
    "Message",
    "data",
    "Data",
]);

/** Unwrap nested data/result/report wrappers and parse JSON string payloads. */
export const normalizeFclReportApiResult = (result) => {
    if (!result || typeof result !== "object") return result;

    let normalized = { ...result };
    let envelope = normalized.data ?? normalized.Data;
    envelope = tryParseJson(envelope);

    if (Array.isArray(envelope)) {
        normalized = { ...normalized, data: { __primaryArray: envelope } };
        return normalized;
    }

    const unwrapKeys = ["data", "Data", "result", "Result", "report", "Report", "payload", "response"];
    let depth = 0;
    while (envelope && typeof envelope === "object" && !Array.isArray(envelope) && depth < 8) {
        const inner = unwrapKeys.map((k) => envelope[k]).find((v) => v != null);
        if (inner == null) break;
        envelope = tryParseJson(inner);
        depth += 1;
    }

    if (envelope && typeof envelope === "object") {
        normalized = { ...normalized, data: envelope };
    }

    return normalized;
};

/** Merge API envelope with root-level report fields (PrimarySummary, BookingList, etc.). */
export const getReportNestedData = (result) => {
    const normalized = normalizeFclReportApiResult(result);
    if (!normalized || typeof normalized !== "object" || Array.isArray(normalized)) {
        return {};
    }

    const envelope = normalized.data ?? normalized.Data;

    if (Array.isArray(envelope)) {
        return { __primaryArray: envelope };
    }

    const nested =
        envelope && typeof envelope === "object" && !Array.isArray(envelope)
            ? { ...envelope }
            : {};

    Object.entries(normalized).forEach(([key, val]) => {
        if (REPORT_ROOT_META_KEYS.has(key)) return;
        if (val !== undefined && val !== null) {
            nested[key] = val;
        }
    });

    return nested;
};

/** Prefer the largest non-meta object array in a payload (deep search). */
export const findLargestReportArray = (source, skipKeys = REPORT_SKIP_ARRAY_KEYS) => {
    const visit = (node, depth) => {
        if (!node || depth > 10) return [];
        let best = [];

        if (Array.isArray(node)) {
            return node.length > 0 && typeof node[0] === "object" ? node : [];
        }

        if (typeof node !== "object") return [];

        Object.entries(node).forEach(([key, val]) => {
            if (skipKeys.has(key)) return;
            if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object") {
                if (val.length > best.length) best = val;
            } else if (val && typeof val === "object") {
                const inner = visit(val, depth + 1);
                if (inner.length > best.length) best = inner;
            }
        });

        return best;
    };

    return visit(getReportNestedData(source), 0);
};

export const pickReportArray = (nested, keys = []) => {
    if (Array.isArray(nested?.__primaryArray)) {
        return nested.__primaryArray;
    }

    let fallback = [];
    for (const key of keys) {
        if (REPORT_SKIP_ARRAY_KEYS.has(key)) continue;
        const arr = nested?.[key];
        if (Array.isArray(arr) && arr.length > 0) return arr;
        if (Array.isArray(arr) && !fallback.length) fallback = arr;
    }
    return fallback;
};

export const buildFclExportBookingParams = (filters = {}) => {
    const params = {
        FromDate: toApiDateTime(filters.FromDate),
        ToDate: toApiDateTime(filters.ToDate, true),
    };
    if (filters.ReportBy) params.ReportBy = filters.ReportBy;
    return params;
};

export const buildFclExportSalespersonParams = (filters = {}) => {
    const params = buildFclExportBookingParams(filters);
    if (filters.SalesPersonId !== undefined && filters.SalesPersonId !== "") {
        params.SalesPersonId = filters.SalesPersonId;
    }
    return params;
};

const baseDateParams = (filters = {}) => ({
    FromDate: toApiDateTime(filters.FromDate),
    ToDate: toApiDateTime(filters.ToDate, true),
});

export const buildFclImportBookingParams = (filters = {}) => ({
    ...baseDateParams(filters),
    ...(filters.ReportBy ? { ReportBy: filters.ReportBy } : {}),
});

export const buildFclImportActiveShipmentParams = (filters = {}) => ({
    ...baseDateParams(filters),
    ...(filters.ReportBy ? { ReportBy: filters.ReportBy } : {}),
});

export const buildFclImportContainerDemurrageParams = (filters = {}) => ({
    ...baseDateParams(filters),
    ...(filters.ReportBy ? { ReportBy: filters.ReportBy } : {}),
});

export const buildFclImportTruckingParams = (filters = {}) => ({
    ...baseDateParams(filters),
    ...(filters.ReportBy ? { ReportBy: filters.ReportBy } : {}),
});

export const buildFclImportStatusAuditParams = (filters = {}) => ({
    ...baseDateParams(filters),
    ...(filters.ReportBy ? { ReportBy: filters.ReportBy } : {}),
});

export const buildFclImportContainerTrackingParams = (filters = {}) => {
    const params = {
        ...baseDateParams(filters),
        ...(filters.ReportBy ? { ReportBy: filters.ReportBy } : {}),
    };
    if (filters.IPageNo) params.iPageNo = filters.IPageNo;
    if (filters.IPageRecords) params.iPageRecords = filters.IPageRecords;
    return params;
};

export const buildFclImportArrivalNoticeParams = (filters = {}) => ({
    ...baseDateParams(filters),
    ...(filters.ReportBy ? { ReportBy: filters.ReportBy } : {}),
});

export const buildFclImportEtaStatusParams = (filters = {}) => baseDateParams(filters);

export const buildFclImportFileCancellationParams = (filters = {}) => ({
    ...baseDateParams(filters),
    ...(filters.ReportBy ? { ReportBy: filters.ReportBy } : {}),
});

export const buildFclImportArrivalNoticePreAlertParams = (filters = {}) => ({
    ...baseDateParams(filters),
    ...(filters.ReportBy ? { ReportBy: filters.ReportBy } : {}),
});

/** @deprecated Use report-specific builders */
export const buildFclImportDateReportParams = buildFclImportBookingParams;

export const extractReportRows = (result) => {
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.Data)) return result.Data;

    const envelope = result?.data ?? result?.Data;
    const nested =
        envelope && typeof envelope === "object" && !Array.isArray(envelope)
            ? { ...envelope }
            : {};

    if (result && typeof result === "object" && !Array.isArray(result)) {
        Object.entries(result).forEach(([key, val]) => {
            if (REPORT_ROOT_META_KEYS.has(key)) return;
            if (val !== undefined && val !== null) nested[key] = val;
        });
    }

    if (Object.keys(nested).length > 0) {
        const listKeys = [
            "bookingList",
            "BookingList",
            "activeShipmentList",
            "ActiveShipmentList",
            "activeShipmentReportList",
            "ActiveShipmentReportList",
            "shipmentList",
            "ShipmentList",
            "demurrageList",
            "containerDemurrageList",
            "ContainerDemurrageList",
            "truckingList",
            "TruckingList",
            "statusAuditList",
            "StatusAuditList",
            "auditList",
            "AuditList",
            "containerTrackingList",
            "ContainerTrackingList",
            "containerList",
            "ContainerList",
            "trackingList",
            "TrackingList",
            "arrivalNoticeList",
            "ArrivalNoticeList",
            "arrivalNoticeReportList",
            "ArrivalNoticeReportList",
            "etaStatusList",
            "ETAStatusList",
            "etaStatusReportList",
            "ETAStatusReportList",
            "etaList",
            "salespersonList",
            "SalespersonList",
            "salesPersonList",
            "reportData",
            "ReportData",
            "reportList",
            "ReportList",
            "items",
            "Items",
            "list",
            "List",
            "rows",
            "Rows",
            "bookings",
            "Bookings",
        ];
        const skipKeys = new Set([
            "containerTeusList",
            "ContainerTeusList",
            "salesPersonTeus",
            "SalesPersonTeus",
            "contactList",
            "ContactList",
            "carrierTeusList",
            "CarrierTeusList",
            "carrierList",
            "CarrierList",
            "bookingComplexitySummary_List",
            "BookingComplexitySummary_List",
            "bookingComplexitySummaryList",
            "BookingComplexitySummaryList",
            "summary",
            "Summary",
            "primarySummary",
            "PrimarySummary",
            "totalTeus",
            "TotalTeus",
        ]);
        let fallback = [];
        for (const key of listKeys) {
            if (skipKeys.has(key)) continue;
            const arr = nested[key];
            if (Array.isArray(arr) && arr.length > 0) return arr;
            if (Array.isArray(arr) && !fallback.length) fallback = arr;
        }
        for (const [key, val] of Object.entries(nested)) {
            if (skipKeys.has(key)) continue;
            if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object") {
                return val;
            }
        }
        if (fallback.length) return fallback;

        const deep = findLargestReportArray(result);
        if (deep.length) return deep;
    }

    if (Array.isArray(result)) return result;
    return findLargestReportArray(result);
};

export const extractReportSummary = (result) => {
    const envelope = result?.data ?? result?.Data;
    const nested =
        envelope && typeof envelope === "object" && !Array.isArray(envelope)
            ? { ...envelope }
            : {};

    if (result && typeof result === "object" && !Array.isArray(result)) {
        Object.entries(result).forEach(([key, val]) => {
            if (REPORT_ROOT_META_KEYS.has(key)) return;
            if (val !== undefined && val !== null) nested[key] = val;
        });
    }

    const candidates = [
        result?.summary,
        result?.Summary,
        nested?.summary,
        nested?.Summary,
        nested?.primarySummary,
        nested?.PrimarySummary,
    ];
    for (const c of candidates) {
        if (c && typeof c === "object" && !Array.isArray(c)) return { ...c };
    }
    return {};
};

const parseTotalRecordValue = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isNaN(num) || num < 0 ? null : num;
};

/** Resolve total record count from API summary, root fields, pagination, or list length. */
export const pickTotalRecordCount = ({
    nested = {},
    result = {},
    summary = {},
    primaryList = [],
} = {}) => {
    const primary = nested?.PrimarySummary ?? nested?.primarySummary ?? {};
    const pagination =
        nested?.pagination ??
        nested?.Pagination ??
        result?.pagination ??
        result?.Pagination ??
        {};

    const candidates = [
        summary?.totalRecords,
        summary?.TotalRecords,
        summary?.totalRecord,
        summary?.TotalRecord,
        nested?.totalRecords,
        nested?.TotalRecords,
        nested?.totalRecord,
        nested?.TotalRecord,
        result?.totalRecords,
        result?.TotalRecords,
        result?.totalRecord,
        result?.TotalRecord,
        primary?.totalRecords,
        primary?.TotalRecords,
        primary?.totalRecord,
        primary?.TotalRecord,
        primary?.TotalBooking,
        primary?.totalBooking,
        primary?.TotalShipment,
        primary?.totalShipment,
        primary?.totalShipments,
        primary?.TotalShipments,
        nested?.recordCount,
        nested?.RecordCount,
        nested?.totalCount,
        nested?.TotalCount,
        nested?.rowCount,
        nested?.RowCount,
        pagination?.totalRecords,
        pagination?.TotalRecords,
        pagination?.totalRecord,
        pagination?.TotalRecord,
        pagination?.total,
        pagination?.Total,
    ];

    for (const value of candidates) {
        const parsed = parseTotalRecordValue(value);
        if (parsed !== null) return parsed;
    }

    return Array.isArray(primaryList) ? primaryList.length : 0;
};

export const formatReportColumnLabel = (key) =>
    String(key)
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, " ")
        .trim();

/** Parse FCL export GetBookingReport response shape. */
export const parseExportBookingReportResponse = (result) => {
    const envelope = result?.data ?? result?.Data;
    const nested = {
        ...(envelope && typeof envelope === "object" && !Array.isArray(envelope) ? envelope : {}),
    };
    if (result && typeof result === "object" && !Array.isArray(result)) {
        Object.entries(result).forEach(([key, val]) => {
            if (REPORT_ROOT_META_KEYS.has(key)) return;
            if (val !== undefined && val !== null) nested[key] = val;
        });
    }
    const containerTeusList =
        nested.containerTeusList ?? nested.ContainerTeusList ?? [];
    const summary =
        nested.summary ??
        nested.Summary ??
        nested.primarySummary ??
        nested.PrimarySummary ??
        {};
    const bookingList = nested.bookingList ?? nested.BookingList ?? [];
    const totalTeusObj = nested.totalTeus ?? nested.TotalTeus ?? {};

    const totalTeusFromContainers = containerTeusList.reduce(
        (sum, row) => sum + (Number(row.totalTues ?? row.TotalTues ?? 0) || 0),
        0
    );

    const totalTeusFromApi =
        totalTeusObj.totalTEUS ??
        totalTeusObj.TotalTEUS ??
        totalTeusObj.totalTeus ??
        totalTeusObj.TotalTeus;

    return {
        bookingList,
        summary: {
            ...summary,
            totalTeus:
                totalTeusFromApi ??
                summary.totalTeus ??
                summary.TotalTeus ??
                (totalTeusFromContainers > 0 ? totalTeusFromContainers : undefined),
        },
        containerTeusList,
    };
};

/** True when ISO string has a non-midnight time (e.g. 14:26:14), not 00:00:00. */
export const reportDateHasTime = (dateStr) => {
    const str = String(dateStr).trim();
    const match = str.match(/T(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!match) return false;
    const [, hours, minutes, seconds = "00"] = match;
    return hours !== "00" || minutes !== "00" || seconds !== "00";
};

/**
 * Report grid dates: "6 Apr 2026", or "6 Apr 2026 2:26 pm" when time is present (not midnight).
 */
export const formatReportSmartDateTime = (dateStr, emptyValue = "-") => {
    if (dateStr === null || dateStr === undefined || dateStr === "") return emptyValue;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr);

    const datePart = d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });

    if (!reportDateHasTime(dateStr)) return datePart;

    const timePart = d.toLocaleTimeString("en-GB", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
    return `${datePart} ${timePart}`;
};

export const formatReportDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const formatted = formatReportSmartDateTime(dateStr, "");
    return formatted;
};

export const formatReportDateTime = (dateStr) => formatReportSmartDateTime(dateStr, "-");

/** @deprecated Use formatReportSmartDateTime — kept for existing imports. */
export const formatReportUsDateTime = (dateStr) => formatReportSmartDateTime(dateStr, "-");

export const formatReportCurrency = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return "-";
    return `$ ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const getExportBookingReportByLabel = (reportBy) => {
    const labels = {
        ETS: "ETS",
        ETA: "ETA",
        BookingDate: "Booking Date",
    };
    return labels[reportBy] || reportBy || "ETS";
};

export const formatReportPercent = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return "-";
    const pct = Math.abs(num) <= 1 ? num * 100 : num;
    return `${pct.toFixed(2)}%`;
};

/** Parse FCL export GetBookingSalespersonReport response shape. */
export const parseExportSalespersonReportResponse = (result) => {
    const envelope = result?.data ?? result?.Data;
    const nested = {
        ...(envelope && typeof envelope === "object" && !Array.isArray(envelope) ? envelope : {}),
    };
    if (result && typeof result === "object" && !Array.isArray(result)) {
        Object.entries(result).forEach(([key, val]) => {
            if (REPORT_ROOT_META_KEYS.has(key)) return;
            if (val !== undefined && val !== null) nested[key] = val;
        });
    }
    const bookingList = nested.bookingList ?? nested.BookingList ?? [];
    const summary = nested.summary ?? nested.Summary ?? {};
    const salesPersonTeus = nested.salesPersonTeus ?? nested.SalesPersonTeus ?? [];
    const contactList = nested.contactList ?? nested.ContactList ?? [];
    const containerTeusList = nested.containerTeusList ?? nested.ContainerTeusList ?? [];
    const totalTeusObj = nested.totalTeus ?? nested.TotalTeus ?? {};
    const totalTeusFromApi =
        totalTeusObj.totalTEUS ?? totalTeusObj.TotalTEUS ?? totalTeusObj.totalTeus ?? totalTeusObj.TotalTeus;

    const totalTeusFromContainers = containerTeusList.reduce(
        (sum, row) => sum + (Number(row.totalTues ?? row.TotalTues ?? 0) || 0),
        0
    );

    return {
        bookingList,
        summary: {
            ...summary,
            totalTeus:
                totalTeusFromApi ??
                summary.totalTeus ??
                summary.TotalTeus ??
                (totalTeusFromContainers > 0 ? totalTeusFromContainers : undefined),
        },
        salesPersonTeus,
        contactList,
        containerTeusList,
    };
};

export const getContainerTypeFromRow = (row) =>
    row?.containeR_TYPE ?? row?.containerType ?? row?.ContainerType ?? row?.CONTAINER_TYPE ?? "-";

export const getContainerTeusFromRow = (row) =>
    row?.totalTues ?? row?.TotalTues ?? row?.totalTEUS ?? row?.TotalTEUS ?? 0;

/** True when FCL report API envelope indicates success. */
export const isFclReportApiSuccess = (result) => {
    if (!result || typeof result !== "object") return false;

    const code = result?.statuscode ?? result?.statusCode ?? result?.StatusCode;
    if (code === 200 || code === 201 || code === "200" || code === "201") return true;

    const statusText = String(result?.status ?? "").toLowerCase();
    if (statusText.includes("success")) return true;
    if (result?.status === true) return true;

    if (extractReportRows(result).length > 0) return true;

    const envelope = result?.data ?? result?.Data;
    if (envelope && typeof envelope === "object") return true;

    return false;
};
